import { useCallback, useEffect, useRef, useState } from 'react';
import { isEditorFloatingMenuOpen } from '../../../../../../../../components/DocEditor/utils/floatingMenuPosition';
import { isAttachmentPreviewOpen } from '../../../../../../../../utils/attachmentPreviewPresence';
import { isVariablesPanelOpen } from '../../../../../../../../utils/variablesPanelPresence';

function stacksEqual(a, b) {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

/**
 * In-place doc navigation stack for mission-context doc sheets.
 * Esc walks back one level or closes the sheet on the first doc.
 * Stack can be restored from / synced to the owning tab (persisted).
 *
 * Tab/mission surface restores sync during render so the first painted frame
 * already has the correct activeDocId — MissionDocSheet must not briefly see
 * null and unmount (that caused skeleton → disappear → reappear).
 */
export function useMissionDocNavigation(taskId, initialStack = [], onStackChange) {
  const incomingStack = Array.isArray(initialStack) ? initialStack : [];
  const externalStackKey = incomingStack.join('\0');
  const sourceKey = `${taskId ?? ''}::${externalStackKey}`;

  const [docStack, setDocStack] = useState(incomingStack);
  const [appliedSourceKey, setAppliedSourceKey] = useState(sourceKey);
  const sheetCloseRef = useRef(null);
  const onStackChangeRef = useRef(onStackChange);
  onStackChangeRef.current = onStackChange;

  // Same-render adjust when the tab/mission surface changes (React-recommended
  // alternative to effectting this and painting a stale frame first).
  let effectiveStack = docStack;
  if (sourceKey !== appliedSourceKey) {
    setAppliedSourceKey(sourceKey);
    if (!stacksEqual(docStack, incomingStack)) {
      setDocStack(incomingStack);
    }
    effectiveStack = incomingStack;
  }

  const activeDocId = effectiveStack.length > 0
    ? effectiveStack[effectiveStack.length - 1]
    : null;

  const commitStack = useCallback((next) => {
    setDocStack(next);
    onStackChangeRef.current?.(next);
  }, []);

  const handleOpenDoc = useCallback((docId) => {
    setDocStack((prev) => {
      const next = prev[prev.length - 1] === docId ? prev : [...prev, docId];
      if (next !== prev) onStackChangeRef.current?.(next);
      return next;
    });
  }, []);

  const handleBack = useCallback(() => {
    setDocStack((prev) => {
      const next = prev.slice(0, -1);
      onStackChangeRef.current?.(next);
      return next;
    });
  }, []);

  const handleCloseSheet = useCallback(() => commitStack([]), [commitStack]);

  useEffect(() => {
    if (activeDocId === null) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (isEditorFloatingMenuOpen()) return;
      if (isVariablesPanelOpen()) return;
      if (isAttachmentPreviewOpen()) return;
      e.stopPropagation();
      setDocStack((prev) => {
        if (prev.length > 1) {
          const next = prev.slice(0, -1);
          onStackChangeRef.current?.(next);
          return next;
        }
        sheetCloseRef.current?.();
        return prev;
      });
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeDocId]);

  return {
    docStack: effectiveStack,
    activeDocId,
    sheetCloseRef,
    handleOpenDoc,
    handleBack,
    handleCloseSheet,
  };
}
